/**
 * Theme Context
 * 
 * Provides theme state and switching functionality throughout the application.
 * Mirrors the AuthContext pattern for consistency.
 * 
 * Features:
 * - Runtime theme switching between peach, greenish, and violetBluish
 * - localStorage persistence with key 'app.theme'
 * - CSS custom properties applied via data-theme attribute
 * - Type-safe theme access via useTheme hook
 * 
 * @module contexts/ThemeContext
 */

import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { themes, DEFAULT_THEME, type Theme, type ThemeName } from '../theme';

interface ThemeContextType {
  theme: Theme;
  themeName: ThemeName;
  setTheme: (themeName: ThemeName) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const THEME_STORAGE_KEY = 'app.theme';

/**
 * Apply theme to DOM by setting CSS custom properties.
 * Uses data-theme attribute for theme-specific styling.
 * 
 * @param theme - Theme object containing palette
 */
function applyThemeToDOM(theme: Theme): void {
  const root = document.documentElement;
  
  // Set data-theme attribute for theme-specific CSS selectors
  root.setAttribute('data-theme', theme.name);
  
  // Apply CSS custom properties from palette
  root.style.setProperty('--app-background', theme.palette.background);
  root.style.setProperty('--app-surface', theme.palette.surface);
  root.style.setProperty('--app-surface-alt', theme.palette.surfaceAlt);
  root.style.setProperty('--app-accent', theme.palette.accent);
  root.style.setProperty('--app-accent-alt', theme.palette.accentAlt);
  root.style.setProperty('--app-text-primary', theme.palette.textPrimary);
  root.style.setProperty('--app-text-secondary', theme.palette.textSecondary);
  root.style.setProperty('--app-text-on-accent', theme.palette.textOnAccent);
  root.style.setProperty('--app-border', theme.palette.border);
  root.style.setProperty('--app-focus-ring', theme.palette.focusRing);
  
  // Apply footer gradient properties
  root.style.setProperty('--app-footer-gradient-start', theme.palette.footerGradientStart);
  root.style.setProperty('--app-footer-gradient-end', theme.palette.footerGradientEnd);
}

/**
 * Load theme name from localStorage.
 * Returns DEFAULT_THEME.name if no valid theme is stored.
 * 
 * @returns Theme name from storage or default
 */
function loadThemeFromStorage(): ThemeName {
  try {
    const storedTheme = localStorage.getItem(THEME_STORAGE_KEY);
    if (storedTheme && storedTheme in themes) {
      return storedTheme as ThemeName;
    }
  } catch (error) {
    console.error('Failed to load theme from storage:', error);
  }
  return DEFAULT_THEME.name;
}

/**
 * Save theme name to localStorage.
 * 
 * @param themeName - Theme name to persist
 */
function saveThemeToStorage(themeName: ThemeName): void {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, themeName);
  } catch (error) {
    console.error('Failed to save theme to storage:', error);
  }
}

/**
 * ThemeProvider component.
 * Wraps the application to provide theme state and switching functionality.
 * 
 * @example
 * ```tsx
 * <ThemeProvider>
 *   <App />
 * </ThemeProvider>
 * ```
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [themeName, setThemeNameState] = useState<ThemeName>(() => loadThemeFromStorage());
  const theme = themes[themeName];

  // Apply theme to DOM whenever it changes
  useEffect(() => {
    applyThemeToDOM(theme);
  }, [theme]);

  /**
   * Set the active theme and persist to localStorage.
   * 
   * @param newThemeName - Name of the theme to activate
   */
  const setTheme = (newThemeName: ThemeName): void => {
    if (newThemeName in themes) {
      setThemeNameState(newThemeName);
      saveThemeToStorage(newThemeName);
    } else {
      console.error(`Invalid theme name: ${newThemeName}`);
    }
  };

  const value: ThemeContextType = {
    theme,
    themeName,
    setTheme,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

/**
 * Hook to access theme context.
 * Must be used within a ThemeProvider.
 * 
 * @throws Error if used outside ThemeProvider
 * @returns Theme context containing current theme and setTheme function
 * 
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { theme, setTheme } = useTheme();
 *   
 *   return (
 *     <div style={{ backgroundColor: theme.palette.background }}>
 *       <button onClick={() => setTheme('greenish')}>
 *         Switch to Greenish
 *       </button>
 *     </div>
 *   );
 * }
 * ```
 */
export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
